import { toTimestamp, type EpgDate } from '$/epg/dates'
import { rangePosition, State, type Range } from '$/epg/programs'
import { type EpgInfo } from '@setplex/tria-api'
import { HOURS_IN_DAY, ONE_DAY_MS, ONE_HOUR_MS } from '~/shared/constants'
import { type LiveProgramInfo } from './index.h'

export const epgDateToRange = (
  from: EpgDate,
  behind: number,
  ahead: number
): Range => {
  const moment = toTimestamp(from)
  const behindDaysMs = Math.ceil(behind / HOURS_IN_DAY) * ONE_DAY_MS
  const aheadDaysMs = (Math.ceil(ahead / HOURS_IN_DAY) + 1) * ONE_DAY_MS // +1 because we need to load programs for today
  return [moment - behindDaysMs, moment + aheadDaysMs]
}

/**
 * Format program to view (epg board or epg in Player)
 */
export const formatProgramToView =
  ({ now }: { now: number }) =>
  (program: Readonly<EpgInfo>): LiveProgramInfo => {
    const position = rangePosition(
      [program.actualStartMs, program.actualStopMs],
      now
    )

    const isCatchupEnabled = program.catchUp?.enabled
    const maxAvailableCatchupStartMs =
      now - (program.catchUp?.duration || 0) * ONE_HOUR_MS
    const isPast = position.state === State.Elapsed
    const isCatchup = isCatchupEnabled
      ? isPast && program.actualStartMs > maxAvailableCatchupStartMs
      : false

    const catchUp = isCatchup
      ? {
          ...program.catchUp,
          catchupInterval: {
            startTime: program.actualStartMs,
            endTime: program.actualStopMs,
          },
        }
      : null

    const isLive = position.state === State.Ongoing
    // catchup should not be empty
    const isEmpty = isCatchup ? false : program.isEmpty

    const programTitle =
      (isEmpty && !isLive) || (isCatchup && !program.name)
        ? `Program ${(program.title || '').split('. ')[1]}`
        : program.name

    return {
      programId: program.id,
      programTitle,
      programDescription: program.description,
      channelTitle: String(program.title),
      startMs: program.startMs,
      stopMs: program.stopMs,
      actualStartMs: program.actualStartMs,
      actualStopMs: program.actualStopMs,
      actualStartSec: program.actualStartSec,
      actualStopSec: program.actualStopSec,
      actualDurationMs: program.actualDurationMs,
      leftMs: program.actualStopMs - now,
      isPast: position.state === State.Elapsed,
      isLive,
      isEmpty,
      isCatchup,
      position,
      catchUp,
    }
  }
